import React, { createContext, useContext, useState, useEffect } from 'react';
import { formsApi } from '../api/api';
import { useErrorHandler } from '../hooks/useErrorHandler';

interface User {
  id: number;
  username: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
  is_power_user: boolean;
  must_change_password: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; user?: User }>;
  logout: () => void;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { handleError, extractErrorMessage } = useErrorHandler();

  useEffect(() => {
    // Skip authentication check for public routes
    const isPublicRoute = window.location.pathname.startsWith('/public/') || 
                         window.location.pathname.startsWith('/anonymous/') ||
                         window.location.pathname === '/login' ||
                         window.location.pathname === '/change-password';
    
    if (isPublicRoute) {
      setLoading(false);
      return;
    }
    
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Try to get current user info to check if logged in
      const response = await formsApi.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; user?: User }> => {
    try {
      // Get CSRF token before authentication
      await formsApi.getCsrfToken();
      
      const response = await formsApi.login(email, password);
      
      // Clear CSRF token after login to ensure fresh token for new session
      formsApi.clearCsrfToken();
      
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const errorMessage = extractErrorMessage(error as any);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await formsApi.logout();
    } catch (error) {
      // Log logout errors but don't show to user
      handleError(error as any, { 
        showNotification: false, 
        logError: true 
      });
    } finally {
      // Clear stored CSRF token to prepare for next login
      formsApi.clearCsrfToken();
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await formsApi.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};